import { Link } from '@tanstack/react-router'
import { PROJECT_STATUS_LABELS, credentialIssues, credentialStats, lastCall, missedWeeks, nextCallOn, openActions, todayIso, weekState } from '../../../shared/projects.js'
import { fmtDate, projectPill } from './fields.jsx'
import styles from './projects.module.css'

/** All projects as cards. */
export function AllProjects({ projects, canEdit, onNew }) {
  const today = todayIso()
  return (
    <>
      <div className={styles.head}>
        <div>
          <h1 className={styles.title}>Projects</h1>
          <p className={styles.sub}>Every client project, its systems, the credentials we still need, and the weekly call record.</p>
        </div>
        {canEdit ? (
          <button type="button" className={styles.primary} onClick={onNew}>
            New project
          </button>
        ) : null}
      </div>
      {projects.length === 0 ? (
        <div className={styles.empty}>{canEdit ? 'No projects yet. Create the first one to start tracking systems, credentials and weekly calls.' : 'No projects yet. An editor can create the first one.'}</div>
      ) : (
        <div className={styles.grid}>
          {projects.map((p) => {
            const cs = credentialStats(p)
            const last = lastCall(p)
            const next = nextCallOn(p)
            const missed = missedWeeks(p, today)
            return (
              <Link key={p.id} to="/projects" search={{ p: p.id }} className={styles.card}>
                <div className={styles.row} style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <h3>{p.name}</h3>
                    <div className={styles.muted}>{[p.client, p.owner].filter(Boolean).join(' · ')}</div>
                  </div>
                  <span className={projectPill(p.status)}>{PROJECT_STATUS_LABELS[p.status]}</span>
                </div>
                <div className={styles.chips}>
                  {p.systems.length ? p.systems.map((s) => <span key={s.id} className={styles.chip}>{s.name}</span>) : <span className={styles.muted}>No systems yet</span>}
                </div>
                <div>
                  <div className={styles.row} style={{ justifyContent: 'space-between', marginBottom: 5 }}>
                    <span className={styles.muted}>Credentials received</span>
                    <b className={styles.mono}>
                      {cs.received}/{cs.total}
                    </b>
                  </div>
                  <div className={styles.bar}>
                    <i style={{ width: `${cs.total ? (cs.received / cs.total) * 100 : 0}%` }} />
                  </div>
                  {cs.blocked ? <div className={styles.flag} style={{ marginTop: 5 }}>{cs.blocked} blocked</div> : null}
                </div>
                <div className={styles.kv}>
                  <div><b>Last call</b>{last ? fmtDate(last.heldOn) : 'None yet'}</div>
                  <div><b>Next call</b>{next ? fmtDate(next) : 'Not set'}</div>
                  <div><b>Open actions</b>{openActions(p).length}</div>
                  <div><b>Missed weeks</b>{missed ? <span className={styles.flag}>{missed}</span> : '0'}</div>
                </div>
                <div className={styles.row}>
                  <span className={`${styles.pill} ${p.sybill.linked ? styles.pOk : styles.pNone}`}>Sybill {p.sybill.linked ? 'linked' : 'not linked'}</span>
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </>
  )
}

const WEEK_PILL = {
  held: ['Held', styles.pOk],
  missed: ['Missed', styles.pBad],
  due: ['Not logged yet', styles.pReq],
  upcoming: ['Upcoming', styles.pNone],
}

/** This week's call state for every project. */
export function WeeklyView({ projects }) {
  const today = todayIso()
  return (
    <>
      <div className={styles.head}>
        <div>
          <h1 className={styles.title}>This week's calls</h1>
          <p className={styles.sub}>Across every project.</p>
        </div>
      </div>
      {projects.length === 0 ? (
        <div className={styles.empty}>No projects yet.</div>
      ) : (
        <div className={styles.panel} style={{ padding: 0 }}>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Project</th>
                  <th>This week</th>
                  <th>Last call</th>
                  <th>Next call</th>
                  <th>Open actions</th>
                  <th>Missed weeks</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => {
                  const state = weekState(p, today)
                  const last = lastCall(p)
                  const missed = missedWeeks(p, today)
                  return (
                    <tr key={p.id}>
                      <td>
                        <Link to="/projects" search={{ p: p.id, tab: 'calls' }}>{p.name}</Link>
                        <div className={styles.muted}>{[p.client, p.owner].filter(Boolean).join(' · ')}</div>
                      </td>
                      <td><span className={`${styles.pill} ${WEEK_PILL[state][1]}`}>{WEEK_PILL[state][0]}</span></td>
                      <td>{last ? fmtDate(last.heldOn) : '–'}</td>
                      <td>{fmtDate(nextCallOn(p))}</td>
                      <td className={styles.mono}>{openActions(p).length}</td>
                      <td>{missed ? <span className={styles.flag}>{missed}</span> : 0}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  )
}

/** Blocked or overdue credentials across every project. */
export function BlockedView({ projects }) {
  const today = todayIso()
  const rows = projects.flatMap((p) => credentialIssues(p, today).map((i) => ({ p, ...i })))
  return (
    <>
      <div className={styles.head}>
        <div>
          <h1 className={styles.title}>Blocked credentials</h1>
          <p className={styles.sub}>Items that are blocked, or past their due date and not yet received.</p>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className={styles.empty}>Nothing is blocked or overdue across your projects.</div>
      ) : (
        <div className={styles.panel} style={{ padding: 0 }}>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Project</th>
                  <th>System</th>
                  <th>What we need</th>
                  <th>Issue</th>
                  <th>Waiting on</th>
                  <th>Due</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ p, system, cred, why }) => (
                  <tr key={cred.id}>
                    <td><Link to="/projects" search={{ p: p.id, tab: 'systems' }}>{p.name}</Link></td>
                    <td>{system.name}</td>
                    <td>{cred.label}</td>
                    <td><span className={`${styles.pill} ${why === 'blocked' ? styles.pBad : styles.pReq}`}>{why === 'blocked' ? 'Blocked' : 'Overdue'}</span></td>
                    <td>{cred.providedBy || '–'}</td>
                    <td>{fmtDate(cred.dueOn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  )
}
